import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BodyClasses,
  ColumnDefinition,
  ColumnDisplayType,
  ColumnSizes,
  GridComponent,
  GridConfiguration,
  OrderByPipe,
  DialogContent
} from 'components';
import { DialogService } from 'src/app/core/services/dialog.service';
import { CodeWrapperService } from 'src/app/core/services/service-wrappers/code-wrapper.service';
import { AdmissionTypeService } from './admission-type.service';
import { take, map } from 'rxjs/operators';
import { NotificationService } from 'src/app/core/services/notification.service';
import { UploadCodesComponent } from '../upload-codes/upload-codes.component';
import { SessionStorageService } from '@core/services/session-storage.service';
import { ImplementationDetailViewModel, ImplementationViewModel, UserDetailsViewModel } from 'data';
import { AdmissionTypeDialogComponent } from './admission-type-dialog/admission-type-dialog.component';
import { selectImplementation } from '@app/features/implementation/state/implementation.selectors';
import { Subscription, Observable } from 'rxjs';
import { ImplementationWrapperService } from '@core/services/service-wrappers/implementation-wrapper.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ImplementationState } from '@app/features/implementation/state/implementation.state';
import * as ImplementationActions from '../../implementation/state/implementation.actions';
import { Store } from '@ngrx/store';

@Component({
  standalone: false,
  selector: 'app-admission-type',
  templateUrl: './admission-type.component.html',
  styleUrls: ['./admission-type.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AdmissionTypeService]
})
export class AdmissionTypeComponent {
  @ViewChild(GridComponent) grid: GridComponent;
  orderPipe: OrderByPipe = new OrderByPipe();
  activeFilter: string = 'active';
  userInfo: UserDetailsViewModel;
  viewOnly = false;
  viewOnlyOptions = [{ id: 'view', label: 'View' }];
  overflowMenuOptions = [
    {
      id: 'edit',
      label: 'Edit'
    }
  ];
  private columnDefinitions: ColumnDefinition[] = [
    {
      id: 'action',
      displayKey: 'action',
      headerText: '',
      displayType: ColumnDisplayType.actionList,
      columnSize: ColumnSizes.tiny,
      bodyClasses: [BodyClasses.alignRight],
      displayOrder: 0,
      overflowMenuOptions: this.overflowMenuOptions,
      overflowMenuClicked: (value, data) => {
        if (value === 'delete') {
          this.deleteEntry(data);
        } else if (value === 'edit') {
          this.openAdmissionTypeModal(data.id);
        } else if (value === 'view') {
          this.openAdmissionTypeModal(data.id, true);
        }
      }
    },
    {
      id: 'code',
      displayKey: 'code',
      headerText: 'Code',
      displayOrder: 1,
      columnSize: ColumnSizes.fixedColumn,
      filterable: true,
      sortable: true,
      sticky: true
    },
    {
      id: 'description',
      displayKey: 'name',
      headerText: 'Description',
      displayOrder: 2,
      columnSize: ColumnSizes.superWide,
      sortable: true,
      filterable: true
    }
  ];
  gridConfig: GridConfiguration = {
    columnDefinitions: this.columnDefinitions,
    displayColumns: this.orderPipe.transform(this.columnDefinitions, 'displayOrder').map((x) => x.id)
  };

  dataSource: AdmissionTypeService;
  constructor(
    private admissionTypeService: AdmissionTypeService,
    public dialog: MatDialog,
    private deleteService: CodeWrapperService,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private sessionStorageService: SessionStorageService,
    private activatedRoute: ActivatedRoute,
    private implementationService: ImplementationWrapperService,
    private store: Store<ImplementationState>,
    private router: Router,
  ) {
    this.userInfo = this.sessionStorageService.getItem('user-info');
    this.viewOnly =
      this.userInfo?.userPermissions?.find((x) => x.permissionType == 'SystemMaintenance')?.accessType !== 'Update';
    if (this.viewOnly) {
      this.columnDefinitions[0].overflowMenuOptions = this.viewOnlyOptions;
    }
    this.dataSource = admissionTypeService;
    this.dataSource.activeFilter = 'active';
  }

  addAdmissionType() {
    this.openAdmissionTypeModal(null);
  }

  openAdmissionTypeModal(admissionTypeId: string, viewOnly = false) {
    const dialog = this.dialog.open(AdmissionTypeDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: { admissionTypeId, viewOnly }
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.grid.loadData();
      }
    });
  }

  deleteEntry(data) {
    const modalContent: DialogContent = {
      header: 'Delete Admission Type',
      body: `Are you sure you want to delete "${data.name}"? This cannot be undone.`,
      cancelButtonText: 'Cancel',
      OKButtonText: 'Delete'
    };
    this.dialogService.showConfirm(modalContent).subscribe((result) => {
      if (result) {
        this.deleteService
          .apiV1CodeCodetypeDeleteIdDelete('AdmissionType', data.id)
          .pipe(
            map((x: any) => x),
            take(1)
          )
          .subscribe((deleteResult) => {
            this.grid.loadData();
            this.notificationService.notifyNow('Admission Type Deleted');
          });
      }
    });
  }

  uploadCodes() {
    const dialog = this.dialog.open(UploadCodesComponent, {
      disableClose: true,
      autoFocus: false,
      height: '600px',
      width: '600px',
      data: { codeType: 'AdmissionType' }
    });

    dialog.afterClosed().subscribe((result) => {
      this.grid.loadData();
    });
  }

  showInactiveChanges(event) {
    this.activeFilter = event.checked ? 'active' : 'inactive';
    this.dataSource.activeFilter = this.activeFilter;
    this.grid.loadData();
  }

  public isImplementation: boolean = false;
  public gonext: boolean = false;
  public itemCount: number = 0;
  private activatedRouteSub: Subscription;
  private itemCountSub: Subscription;
  private implementation$: Observable<ImplementationViewModel> = this.store.select(selectImplementation);
  private implementationSub: Subscription;
  private implementation: ImplementationViewModel = null;
  private implementationDetail: ImplementationDetailViewModel = null;
  private nextImplementationDetail: ImplementationDetailViewModel = null;
  private STEP_CODE = 'admission-types';

  implementationSubscribe() {
    this.activatedRouteSub = this.activatedRoute.data?.subscribe((data) => {
      this.isImplementation = data.implementation;

      if (this.isImplementation) {
        this.itemCountSub = this.admissionTypeService.itemCountSubject.subscribe((c) => (this.itemCount = c));
        this.implementationSub = this.implementation$.subscribe((i) => {
          this.implementation = i;
          this.implementationDetail = this.implementation.details.find(
            (d) => d.implementationStepCode == this.STEP_CODE
          );
          this.nextImplementationDetail = this.implementation.details.find(
            (d) => d.order == this.implementationDetail.order + 1
          );
        });
      }
    });
    this.store.dispatch(ImplementationActions.updateImplementationStep({ stepCode: this.STEP_CODE }));
  }

  implementationUnsubscribe() {
    if (this.isImplementation) {
      this.activatedRouteSub.unsubscribe();
      this.itemCountSub.unsubscribe();
      this.implementationSub.unsubscribe();
    }
  }

  next(): void {
    if (this.implementationDetail.status == 'InProgress') {
      this.gonext = true;
      this.implementationService.apiV1ImplementationCompleteStepCodePost(this.STEP_CODE).subscribe(
        () => {
          this.gonext = false;
          this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
        },
        () => (this.gonext = false)
      );
    } else {
      this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
    }
  }

  ngOnInit(): void {
    this.implementationSubscribe();
  }
  ngOnDestroy(): void {
    this.implementationUnsubscribe();
  }
}
